import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Button, IconButton, Dialog, DialogTitle,
    DialogContent, DialogActions, TextField, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TablePagination, Tooltip,
    InputAdornment, Snackbar, Alert, CircularProgress, Chip,
} from '@mui/material';
import { Edit, Delete, Add, Search, AccountBalance } from '@mui/icons-material';
import { getMinistries, createMinistry, updateMinistry, deleteMinistry, Ministry } from '../../services/MinistryService';
import { useDebounce } from '../../utils/debounce';

const Ministries = () => {
    const [ministries, setMinistries] = useState<Ministry[]>([]);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(15);
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 500);

    const [openDialog, setOpenDialog] = useState(false);
    const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
    const [selectedMinistry, setSelectedMinistry] = useState<Ministry | null>(null);
    const [formData, setFormData] = useState({ ministry_name: '', ministry_code: '' });
    const [formErrors, setFormErrors] = useState<{ ministry_name?: string }>({});

    const [deleteDialog, setDeleteDialog] = useState(false);
    const [ministryToDelete, setMinistryToDelete] = useState<Ministry | null>(null);

    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

    useEffect(() => {
        fetchMinistries();
    }, [page, rowsPerPage, debouncedSearch]);

    const fetchMinistries = async () => {
        try {
            setLoading(true);
            const response = await getMinistries({
                page: page + 1,
                per_page: rowsPerPage,
                search: debouncedSearch
            });
            setMinistries(response.data.data.data);
            setTotal(response.data.data.total);
        } catch (error) {
            showSnackbar('Failed to fetch ministries', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (ministry?: Ministry) => {
        setFormErrors({});
        if (ministry) {
            setDialogMode('edit');
            setSelectedMinistry(ministry);
            setFormData({
                ministry_name: ministry.ministry_name,
                ministry_code: ministry.ministry_code || ''
            });
        } else {
            setDialogMode('create');
            setSelectedMinistry(null);
            setFormData({ ministry_name: '', ministry_code: '' });
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setFormData({ ministry_name: '', ministry_code: '' });
        setSelectedMinistry(null);
        setFormErrors({});
    };

    const validateForm = () => {
        const errors: { ministry_name?: string } = {};
        if (!formData.ministry_name.trim()) {
            errors.ministry_name = 'Ministry Name is required';
        }
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        try {
            if (dialogMode === 'create') {
                await createMinistry(formData);
                showSnackbar('Ministry created successfully', 'success');
            } else if (dialogMode === 'edit' && selectedMinistry) {
                await updateMinistry(selectedMinistry.id, formData);
                showSnackbar('Ministry updated successfully', 'success');
            }
            handleCloseDialog();
            fetchMinistries();
        } catch (error: any) {
            showSnackbar(error.response?.data?.message || 'Operation failed', 'error');
            if (error.response?.data?.errors) {
                const apiErrors: any = {};
                if (error.response.data.errors.ministry_name) {
                    apiErrors.ministry_name = error.response.data.errors.ministry_name[0];
                }
                setFormErrors(apiErrors);
            }
        }
    };

    const handleDeleteClick = (ministry: Ministry) => {
        setMinistryToDelete(ministry);
        setDeleteDialog(true);
    };

    const handleConfirmDelete = async () => {
        if (!ministryToDelete) return;
        try {
            await deleteMinistry(ministryToDelete.id);
            showSnackbar('Ministry deleted successfully', 'success');
            setDeleteDialog(false);
            setMinistryToDelete(null);
            fetchMinistries();
        } catch (error: any) {
            showSnackbar(error.response?.data?.message || 'Failed to delete ministry', 'error');
            setDeleteDialog(false);
            setMinistryToDelete(null);
        }
    };

    const showSnackbar = (message: string, severity: 'success' | 'error') => {
        setSnackbar({ open: true, message, severity });
    };

    return (
        <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h4" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AccountBalance color="primary" fontSize="large" /> Ministries
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Manage ministries available for development activities
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => handleOpenDialog()}
                    sx={{ borderRadius: 2, textTransform: 'none', px: 3, py: 1 }}
                >
                    Add Ministry
                </Button>
            </Box>

            <Paper sx={{ p: 2, mb: 3, borderRadius: 2, display: 'flex', gap: 2 }}>
                <TextField
                    size="small"
                    placeholder="Search ministries..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ width: 300 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search fontSize="small" color="action" />
                            </InputAdornment>
                        ),
                    }}
                />
            </Paper>

            <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Table sx={{ minWidth: 650 }} aria-label="ministries table">
                        <TableHead sx={{ bgcolor: 'grey.50' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Ministry Name</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Ministry Code</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {ministries.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                                        <Typography color="text.secondary">No ministries found</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                ministries.map((row) => (
                                    <TableRow key={row.id} hover>
                                        <TableCell>{row.id}</TableCell>
                                        <TableCell sx={{ fontWeight: 500 }}>{row.ministry_name}</TableCell>
                                        <TableCell>
                                            {row.ministry_code ? (
                                                <Chip label={row.ministry_code} size="small" variant="outlined" />
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">-</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Edit">
                                                <IconButton color="primary" size="small" onClick={() => handleOpenDialog(row)}>
                                                    <Edit fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete">
                                                <IconButton color="error" size="small" onClick={() => handleDeleteClick(row)}>
                                                    <Delete fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                )}
                <TablePagination
                    rowsPerPageOptions={[10, 15, 25, 50]}
                    component="div"
                    count={total}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                />
            </TableContainer>

            {/* Create/Edit Dialog */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>{dialogMode === 'create' ? 'Add Ministry' : 'Edit Ministry'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            label="Ministry Name"
                            fullWidth
                            required
                            value={formData.ministry_name}
                            onChange={(e) => setFormData({ ...formData, ministry_name: e.target.value })}
                            error={!!formErrors.ministry_name}
                            helperText={formErrors.ministry_name}
                        />
                        <TextField
                            label="Ministry Code (Optional)"
                            fullWidth
                            value={formData.ministry_code}
                            onChange={(e) => setFormData({ ...formData, ministry_code: e.target.value })}
                            placeholder="e.g. MIN-01"
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button onClick={handleCloseDialog} color="inherit">Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained" color="primary">
                        {dialogMode === 'create' ? 'Create' : 'Save Changes'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Delete Ministry</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete the ministry "{ministryToDelete?.ministry_name}"? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button onClick={() => setDeleteDialog(false)} color="inherit">Cancel</Button>
                    <Button onClick={handleConfirmDelete} variant="contained" color="error">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Ministries;
